import { useRef } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Rocket } from "lucide-react";
import { Reveal } from "@/components/common/Reveal";
import { useLead } from "@/components/common/LeadModal";

const METIERS = [
  {
    nom: "Conducteur routier de marchandises",
    image: "https://images.pexels.com/photos/20862827/pexels-photo-20862827.jpeg?auto=compress&cs=tinysrgb&w=900",
  },
  {
    nom: "Conducteur de transport en commun",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/9fhhog7f_pexels-chuck-15812410.webp",
  },
  {
    nom: "Enseignant de la conduite (ECSR)",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/vgblzjsk_pexels-gustavo-fring-4895404.webp",
  },
  {
    nom: "Conducteur de matières dangereuses",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/jkr9r3ax_c-xload-2015_news-secondary_444x444px_fr.jpg",
  },
  {
    nom: "Chauffeur d'autocar & scolaire",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/5s0mw7yt_Resized201807231345247158jpg_5c34dc4541c76.jpg",
  },
  {
    nom: "Formateur groupe lourd",
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/8rbbsyee_image-4741.jpg",
  },
];

export default function MetiersCarousel() {
  const trackRef = useRef(null);
  const { openLead } = useLead();

  const scrollBy = (dir) => {
    if (trackRef.current) trackRef.current.scrollBy({ left: dir * 380, behavior: "smooth" });
  };

  return (
    <section className="bg-white py-24 lg:py-32" data-testid="metiers">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <Rocket className="mx-auto h-9 w-9 text-brand-red" />
          <h2 className="mt-4 font-display text-4xl font-black uppercase leading-[0.95] tracking-[-0.03em] text-brand-blue sm:text-5xl">
            Qualification en poche,<br />lancez votre carrière !
          </h2>
          <p className="mt-5 text-lg text-slate-500">
            Découvrez les métiers du transport qui vous attendent après votre formation chez Educaser.
            Des secteurs qui recrutent, partout en France.
          </p>
        </Reveal>
      </div>

      <div className="relative mt-14">
        <div
          ref={trackRef}
          className="no-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth px-5 lg:px-[max(2rem,calc((100vw-1400px)/2+2rem))]"
        >
          {METIERS.map((m, i) => (
            <motion.div
              key={m.nom}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: (i % 4) * 0.06 }}
              className="group relative w-[300px] shrink-0 snap-start sm:w-[360px]"
              data-testid={`metier-${i}`}
            >
              <div className="relative h-[440px] overflow-hidden rounded-3xl">
                <img src={m.image} alt={m.nom} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-blue/70 to-transparent" />
              </div>
              <h3 className="mt-4 font-display text-lg font-extrabold uppercase tracking-tight text-brand-blue transition-colors group-hover:text-brand-red">
                {m.nom}
              </h3>
            </motion.div>
          ))}
        </div>

        <div className="mx-auto mt-10 flex max-w-[1400px] items-center justify-center gap-3 px-5 lg:justify-start lg:px-8">
          <button onClick={() => scrollBy(-1)} className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue text-white transition-colors hover:bg-brand-red" aria-label="Précédent" data-testid="metier-prev">
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button onClick={() => scrollBy(1)} className="flex h-12 w-12 items-center justify-center rounded-full bg-brand-blue text-white transition-colors hover:bg-brand-red" aria-label="Suivant" data-testid="metier-next">
            <ChevronRight className="h-5 w-5" />
          </button>
          <button onClick={() => openLead("rappel")} className="ml-3 rounded-full border-2 border-brand-blue px-6 py-3 font-bold text-brand-blue transition-colors hover:bg-brand-blue hover:text-white" data-testid="metier-cta">
            Être rappelé
          </button>
        </div>
      </div>
    </section>
  );
}
